<template>
  <DashboardItem
    :title="titleText"
    :loaded="loaded"
  >
    <template #content>
      <p v-if="!isMobile">
        Need to insure something else?
      </p>
      <template v-if="alternateAddNewProductMarkup">
        <IgniteMarkdown>
          {{ alternateAddNewProductMarkup }}
        </IgniteMarkdown>
      </template>
      <template v-else>
        <b-button
          v-for="product in products"
          :key="product.uniqueId"
          :icon-left="getButtonIcon(product).icon"
          :icon-pack="getButtonIcon(product).pack"
          :label="getInsureProductActionLabel(product)"
          @click="goToProduct(product)"
        />
      </template>
    </template>
  </DashboardItem>
</template>

<script lang="ts">
import type ProductEnum from '@/services/enums/ProductEnum'
import type Account from '@/services/models/Account'
import type Product from '@/services/models/Product'
import type Icon from '@/view-models/Icon'
import {
  Component,
  Vue,
} from 'vue-property-decorator'
import { getProductIcon, getProductRoute } from '@/composables/product-utils'
import { goToCustomerPortalJourneyForProduct } from '@/customer-portal'
import { PWAUtils } from '@/plugins/pwa-utils'
import useAccountStore from '@/stores/account-store'
import usePoliciesStore from '@/stores/policies-store'
import DashboardItem from './DashboardItem.vue'

@Component({
  components: {
    DashboardItem,
    LinkConfigButton: () => import('../LinkConfigButton.vue'),
    IgniteMarkdown: () => import('@/components/controls/IgniteMarkdown.vue'),
  },
})
export default class Products extends Vue {
  getProductIcon = getProductIcon

  products: Product[] = []

  loaded = false

  async mounted(): Promise<void> {
    const response = await this.$services.productService.getAll()
    this.products = response.results
    this.loaded = this.products.length > 0 || (!!this.alternateAddNewProductMarkup)
  }

  goToProduct(product: Product) {
    if (this.$configurations.newCustomerPortalForProduct(product.uniqueId as ProductEnum)) {
      goToCustomerPortalJourneyForProduct(product.uniqueId as ProductEnum, 'nb')
      return
    }

    this.$router.push(getProductRoute(product))
  }

  getButtonIcon(product: Product): Icon {
    return this.getProductIcon(product.uniqueId as ProductEnum)
  }

  get isMobile(): boolean {
    return PWAUtils.isMobile()
  }

  get account(): Account | null {
    const accountStore = useAccountStore()
    return accountStore.account
  }

  getInsureProductActionLabel(product: Product): string {
    if (usePoliciesStore().policies?.some(r => r.product === product.uniqueId))
      return this.$configurations.navLinkForAdditionalProduct(product.uniqueId as ProductEnum) ?? `Insure additional ${product.displayName}`

    return this.$configurations.insureProductAction(product.uniqueId as ProductEnum)?.label ?? `Insure a ${product.displayName}`
  }

  get alternateAddNewProductMarkup(): string {
    return this.$configurations.alternateAddNewProductMarkup
  }

  get titleText(): string {
    return this.isMobile
      ? 'Need to insure something else?'
      : `Welcome back${this.account ? `, ${this.account.firstName}` : ''}`
  }
}
</script>
